import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Heading,
  HStack,
  List,
  ListItem,
  // Tag,
  // TagLabel,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import Button from "components/Button/Button";
import Card, { getCardScheme } from "components/Card/Card";
import Input from "components/Input/Input";
import { NumberInputWithMax } from "components/Input/NumberInput";
import PinInput from "components/Input/PinInput";
import Confirmation from "components/Modals/p2p/Confirmation";
import SuccessModal from "components/Modals/Success";
import Select from "components/Select/Select";
import { useSteps } from "components/Steps/provider";
import Steps, { StepContent } from "components/Steps/Steps";
import constant from "config/constant";
import useWalletBalance from "hooks/useWalletBalance";
import { isEmpty, toUpper } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  useCreateCardMutation,
  useGetFirstRequestCheckQuery,
  useGetLGAsQuery,
  useGetUserCardProfileQuery,
} from "services/cardsApi";
import getError from "util/getError";

import toCoinLocale from "util/toCoinLocale";
import toPrecision from "util/toPrecision";

function Step1({ state, handleState, handleExtraData, reset, cards }) {
  const { onNext } = useSteps();
  const minimumFundingAmount = 10; // in usd
  const maximumFundingAmount = 10000; // in usd
  const fundFee = 1.5; // in usd
  const maintenanceFee = 0.42; // in usd

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: firstRequestCheck } = useGetFirstRequestCheckQuery();

  const { data: cardProfile } = useGetUserCardProfileQuery();

  const { data: lgaData, isLoading: isLGALoading } = useGetLGAsQuery(
    state?.extraData?.state,
    { skip: !state?.extraData?.state }
  );

  console.log({ lgaData });

  const userState = useMemo(() => cardProfile?.data?.state, [cardProfile]);

  const lgas = useMemo(() => lgaData?.lgas ?? [], [lgaData]);

  const extraData = useMemo(() => state?.extraData ?? {}, [state]);

  console.log("User cards profile", cardProfile);
  console.log("Request card state", state);

  const { isLoading: isWalletLoading, getBalance } = useWalletBalance();

  const missingFields = useMemo(
    () => cardProfile?.missingFields,
    [cardProfile]
  );

  const cardCost = useMemo(() => {
    if (cards?.length > 0) return 1;
    if (firstRequestCheck && firstRequestCheck?.firstTime) return 0;
    if (firstRequestCheck && firstRequestCheck?.fee)
      return firstRequestCheck?.fee;
    return 1;
  }, [cards, firstRequestCheck]);

  const fundingFee = useMemo(
    () => !!state?.amount && +state?.amount * (fundFee / 100),
    [state?.amount]
  );

  const balance = useMemo(
    () => getBalance(state?.wallet),
    [getBalance, state?.wallet]
  );

  const isBvnValid = useMemo(() => {
    if (!(missingFields ?? []).includes("bvn")) return true;
    else return !!extraData?.bvn && extraData?.bvn?.length === 11;
  }, [extraData, missingFields]);

  console.log("is bvn valid", isBvnValid);

  const isInsufficientBalance = useMemo(
    () => !!state?.amount && +state?.amount > balance,
    [state?.amount, balance]
  );

  const isLessThanMinimum = useMemo(
    () => !!state?.amount && +state?.amount < minimumFundingAmount,
    [state?.amount]
  );

  const isMoreThanMaximum = useMemo(
    () => !!state?.amount && +state?.amount > maximumFundingAmount,
    [state?.amount]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext && onNext();
  };

  const isDisabled = useMemo(
    () =>
      !(state?.currency && state?.type && state?.wallet && state?.amount) ||
      isWalletLoading ||
      getBalance(state?.wallet) < cardCost ||
      !isBvnValid ||
      isLessThanMinimum ||
      isMoreThanMaximum ||
      !state?.agreedToLimitations,
    [
      state,
      isWalletLoading,
      getBalance,
      cardCost,
      isBvnValid,
      isLessThanMinimum,
      isMoreThanMaximum,
    ]
  );

  const handleAgreement = (e) => {
    if (state?.agreedToLimitations) {
      handleState({ agreedToLimitations: false, preAgreement: false });
      return;
    }
    onOpen();
    // handleState({ agreedToLimitations: e.target.checked });
  };

  const handleConfirmationCancel = () => {
    onClose();
    handleState({ agreedToLimitations: false, preAgreement: false });
  };

  return (
    <VStack maxW="container.sm" m="0 auto">
      <Box as="form" onSubmit={handleSubmit}>
        <VStack>
          <Card colorScheme={getCardScheme(state?.card?.type)} />
        </VStack>

        {(missingFields ?? []).map((field) => {
          return (
            <>
              {field !== "lga" && (
                <FormControl mt="20px !important">
                  <Box
                    as="label"
                    display="inline-block"
                    fontSize=".85em"
                    fontWeight="400"
                    mb="8px"
                    color="#4E4B66"
                    textTransform="uppercase"
                  >
                    {field}
                  </Box>
                  <Input
                    isRequired
                    type={field === "bvn" ? "number" : "text"}
                    placeholder={`Enter ${toUpper(field)}`}
                    value={extraData[field] ?? ""}
                    onChange={(e) =>
                      handleExtraData({ [field]: e.target.value })
                    }
                  />

                  {!!extraData?.bvn &&
                    (extraData?.bvn?.length < 11 ||
                      extraData?.bvn?.length > 11) &&
                    field === "bvn" && (
                      <FormHelperText fontSize="sm" color="red.400">
                        BVN is not valid
                      </FormHelperText>
                    )}
                </FormControl>
              )}

              {field === "lga" && (
                <>
                  <FormControl mt="20px !important">
                    <Select
                      label="State"
                      placeholder="Select state"
                      value={extraData["state"] ?? ""}
                      onChange={(e) =>
                        handleExtraData({ state: e.target.value })
                      }
                    >
                      {!!userState && (
                        <option
                          key={`state-1`}
                          value={String(userState).toLowerCase()}
                        >
                          {userState}
                        </option>
                      )}
                    </Select>
                  </FormControl>

                  <FormControl mt="20px !important">
                    <Select
                      label="LGA"
                      isLoading={isLGALoading}
                      placeholder="Select LGA"
                      value={extraData[field] ?? ""}
                      onChange={(e) =>
                        handleExtraData({ [field]: e.target.value })
                      }
                    >
                      {lgas?.map((lga, i) => (
                        <option
                          key={`lga-${i}`}
                          value={String(lga).toLowerCase()}
                        >
                          {lga}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </>
          );
        })}

        {/* {missingFields?.includes("lga") && (
          
        )} */}

        {!isEmpty(missingFields) && (
          <Box pt="30px !important">
            <Divider />
          </Box>
        )}

        <FormControl mt="30px !important">
          <Select
            label="Currency"
            placeholder="Select Currency"
            value={state?.currency ?? ""}
            onChange={(e) => handleState({ currency: e.target.value })}
          >
            <option key={`currency-1`} value="usd">
              USD
            </option>
          </Select>
        </FormControl>

        <FormControl mt="20px !important">
          <Select
            label="Type"
            placeholder="Select Type"
            value={state?.type ?? ""}
            onChange={(e) => handleState({ type: e.target.value })}
          >
            <option key={`type-1`} value="virtual">
              Virtual
            </option>
          </Select>

          <FormHelperText fontSize="sm">
            Virtual card cost <Box as="strong">${cardCost}</Box>
          </FormHelperText>
        </FormControl>

        <FormControl mt="20px !important">
          <Select
            label="Payment Wallet"
            isInvalid={getBalance(state?.wallet) < cardCost}
            placeholder="Select Wallet"
            value={state?.wallet ?? ""}
            onChange={(e) => handleState({ wallet: e.target.value })}
          >
            {constant.stableCoins.map((coin) => (
              <option key={`${coin}-wallet`} value={coin}>
                {toCoinLocale(coin)}
              </option>
            ))}
          </Select>

          {isInsufficientBalance && (
            <FormHelperText fontSize="xs" color="red.400">
              Insufficient balance
            </FormHelperText>
          )}

          {state?.wallet && (
            <FormHelperText fontSize="sm">
              Available balance:{" "}
              <Box
                as="strong"
                color={isInsufficientBalance ? "red.400" : "var(--primary)"}
              >
                {toPrecision(balance, state?.wallet)}
              </Box>
            </FormHelperText>
          )}
        </FormControl>

        {state?.wallet && (
          <FormControl mt="20px !important">
            <Box
              as="label"
              display="inline-block"
              fontSize=".85em"
              fontWeight="400"
              mb="8px"
              color="#4E4B66"
            >
              Fund Amount (Amount to be funded to this card)
            </Box>
            <NumberInputWithMax
              isInvalid={
                isInsufficientBalance || isLessThanMinimum || isMoreThanMaximum
              }
              value={state?.amount ?? ""}
              onMax={() => handleState({ amount: Number(balance).toFixed(2) })}
              onChange={(e) => handleState({ amount: e })}
            />

            <FormHelperText fontSize="sm">
              <HStack>
                <Text>Funding fee({fundFee}%):</Text>
                {/* <Tag colorScheme="green">
                  <TagLabel>Maintenance Fee (${maintenanceFee})</TagLabel>
                </Tag> */}
                <Box as="strong">${Number(fundingFee).toFixed(2) ?? 0}</Box>
              </HStack>
            </FormHelperText>
            <FormHelperText fontSize="sm">
              Maintenance Fee:{" "}
              <Box
                as="strong"
                // color={isLessThanMinimum ? "red.400" : "inherit"}
              >
                ${Number(maintenanceFee).toFixed(2)}
              </Box>
            </FormHelperText>
            <FormHelperText fontSize="sm">
              Minimum funding amount:{" "}
              <Box
                as="strong"
                color={isLessThanMinimum ? "red.400" : "inherit"}
              >
                ${Number(minimumFundingAmount).toFixed(2)}
              </Box>
            </FormHelperText>

            {isMoreThanMaximum && (
              <FormHelperText fontSize="sm">
                Maximum funding amount:{" "}
                <Box
                  as="strong"
                  color={isMoreThanMaximum ? "red.400" : "inherit"}
                >
                  ${Number(maximumFundingAmount).toFixed(2)}
                </Box>
              </FormHelperText>
            )}
          </FormControl>
        )}

        <FormControl mt="38px">
          <FormHelperText fontWeight="500">
            <HStack
              justifyContent={["flex-start", "space-between"]}
              alignItems={["flex-start", "flex-start", "center"]}
              flexDir={["column", "column", "row"]}
            >
              {console.log({ state })}
              <Checkbox
                // dir="rtl"
                isChecked={state?.agreedToLimitations}
                onChange={handleAgreement}
                sx={{
                  "chakra-checkbox__control[data-focus]": {
                    shadow: "0 0 0 3px var(--focusColor)",
                  },
                  ".chakra-checkbox__control[data-checked], .chakra-checkbox__control[data-checked].chakra-checkbox__control[data-hover]":
                    {
                      bg: "var(--primary)",
                      borderColor: "var(--primary)",
                    },
                }}
              >
                I am aware of the terms and conditions of this card.
              </Checkbox>
            </HStack>
          </FormHelperText>
        </FormControl>

        <Button
          isLoading={isWalletLoading}
          disabled={isDisabled}
          w="100%"
          minH="64px"
          mt="32px"
          type="submit"
          // onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>

      <Confirmation
        title="Terms and Conditions"
        isOpen={isOpen}
        onClose={handleConfirmationCancel}
        onConfirm={() => {
          handleState({ agreedToLimitations: true });
          onClose();
        }}
        _content={{ minW: "520px" }}
        confirmDisabled={!state?.preAgreement}
      >
        <Heading as="h5" size="sm">
          This crypto virtual card has the following limitations at the moment:
        </Heading>
        <List sx={{ li: { my: "18px" } }}>
          <ListItem>
            1. BVN is needed to create your card for compliance reasons
          </ListItem>
          <ListItem>2. 1.5% fee for every funding on card</ListItem>
          <ListItem>4. Minimum amount of funding on card is $10</ListItem>
          <ListItem>5. Maximum amount a card can hold is $10,000</ListItem>
          <ListItem>
            6. Monthly maintenance fee for card holding is $0.42
          </ListItem>
          <ListItem>
            7. Card creation fee is $1 (After first card creation)
          </ListItem>
          <ListItem>
            8. Transaction pin is required to complete card transaction
          </ListItem>
          <ListItem>
            <Checkbox
              // dir="rtl"
              isChecked={state?.preAgreement}
              onChange={(e) => handleState({ preAgreement: e.target.checked })}
              alignItems="flex-start"
              colorScheme="green"
              sx={{
                ".chakra-checkbox__control": {
                  top: "5px",
                  pos: "relative",
                },
                "chakra-checkbox__control[data-focus]": {
                  shadow: "0 0 0 3px var(--focusColor)",
                },
                ".chakra-checkbox__control[data-checked], .chakra-checkbox__control[data-checked].chakra-checkbox__control[data-hover]":
                  {
                    bg: "var(--primary)",
                    borderColor: "var(--primary)",
                  },
              }}
            >
              I am aware of the terms and conditions of this card and want to
              proceed to create the card
            </Checkbox>
          </ListItem>
        </List>
      </Confirmation>
    </VStack>
  );
}

const Step2 = ({ state, setState, reset }) => {
  const pinMax = 6;
  const isLoading = false;

  // const [pin, setPin] = useState("");
  // const [show, setShow] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const { setStep } = useSteps();
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [, setHasCompleteValues] = useState(false);

  // const handleClick = () => setShow(!show);

  const [
    createCard,
    {
      data: createCardData,
      isLoading: isCreatingCard,
      error,
      isError,
      isSuccess,
    },
  ] = useCreateCardMutation();

  const handlePin = (pin) => {
    const isValid = String(state?.pin ?? "").length === pinMax;
    if (!isValid) setHasCompleteValues(false);
    setState({ pin });
  };

  const handleSubmit = () => {
    // console.log("STATE STEP3", state);
    const reqData = {
      pin: state?.pin,
      coin: state?.wallet,
      fundAmount: +state?.amount,
      extraData: state?.extraData ?? {},
    };

    console.log("CREATE CARD REQUEST", reqData);
    createCard(reqData);
  };

  const handleSuccessModalClose = () => {
    onClose();
    setStep(0);
    history.replace("/dashboard/cards");
  };

  useEffect(() => {
    if (isSuccess && createCardData) {
      onOpen();
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [isSuccess, error, createCardData, isError, onOpen, toast]);

  const isDisabled = () =>
    isLoading ||
    isCreatingCard ||
    !state?.pin ||
    (state?.pin ?? "")?.length < 6;

  return (
    <VStack maxW="xs" m="0 auto">
      <FormControl>
        <Box
          w="100%"
          textAlign="center"
          mb="24px"
          as="label"
          d="inline-block"
          fontSize="md"
          color="#282930"
          fontWeight="600"
        >
          Enter your 6 digit Pin
        </Box>
        <PinInput
          isRequired
          limit={pinMax}
          size="lg"
          onChange={handlePin}
          onComplete={() => setHasCompleteValues(true)}
        />
      </FormControl>

      <Button
        isLoading={isLoading || isCreatingCard}
        disabled={isDisabled()}
        w="100%"
        minH="64px"
        mt="72px !important"
        onClick={handleSubmit}
      >
        Submit
      </Button>

      <Button
        textTransform="unset"
        size="sm"
        variant="ghost"
        bg="transparent"
        color="var(--primary)"
        fontWeight="400"
        mt="20px !important"
        onClick={() => history.push("/dashboard/account/edit?tab=security")}
      >
        Don’t have a Pin? Create one here
      </Button>

      <SuccessModal
        isOpen={isOpen}
        onClose={handleSuccessModalClose}
        title="Success"
        message="Card was created successfully"
      />
    </VStack>
  );
};

export default function RequestVirtualCardv2({
  cards,
  state,
  handleState,
  reset,
}) {
  // const [state, setState] = useState({
  //   agreedToLimitations: false,
  //   preAgreement: false,
  // });

  const steps = [{ label: "Step 1" }, { label: "Finish" }];

  // const handleState = (newState) => {
  //   setState({ ...state, ...newState });
  // };

  const handleExtraData = (newState) => {
    handleState({ extraData: { ...state?.extraData, ...newState } });
  };

  return (
    <Steps
      type="bullet"
      steps={steps}
      mt="0px"
      _btnContainer={{
        // mx: ["24px", 0],
        mt: 0,
        justifyContent: "center",
      }}
    >
      <StepContent mt="60px">
        <Step1
          state={state}
          cards={cards}
          reset={reset}
          handleState={handleState}
          handleExtraData={handleExtraData}
        />
        <Step2 state={state} reset={reset} setState={handleState} />
      </StepContent>
    </Steps>
  );
}
